/**
 * This is the entry point for Feature Hub App integration
 */

import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices } from '@feature-hub/core';
import type { Logger } from '@feature-hub/logger';
import { ReactFeatureApp } from '@feature-hub/react';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import { RenderMode } from '@oneaudi/render-mode-service';
import type { InPageNavigationServiceV1 } from '@volkswagen-onehub/audi-in-page-navigation-service';
import { defineInPageNavigationService } from '@volkswagen-onehub/audi-in-page-navigation-service';
import React from 'react';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import App from './FeatureApp';
import { FeatureAppContextProvider } from './FeatureAppContext';
import { Content } from './FeatureAppTypes';
import type { AnchorProps } from './components/AnchorTypes';
import { createId } from './utils/createId';
import { mapHeadlessContent } from './utils/mapHeadlessContent';
import { getDesignLibrary } from '../alpha-helpers';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-content-service': ContentServiceV1;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
  readonly 'audi-in-page-navigation-service': InPageNavigationServiceV1;
}

/**
 * It holds a function to retrieve the State needed by the feature app when
 * it´s rendered on the server
 * It holds the State object created from the serialized state on the client
 */
export type AsyncStateHolder = (() => Promise<AnchorProps | undefined>) | AnchorProps | undefined;

export const createInitialState = (
  { anchorName: title }: Content,
  renderMode: RenderMode,
): Promise<AnchorProps | undefined> => {
  try {
    const result: AnchorProps = {
      id: createId(title),
      title,
      editMode: renderMode === RenderMode.EDIT,
    };
    return Promise.resolve(result);
  } catch {
    return Promise.resolve(undefined);
  }
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'audi-in-page-navigation-service': '^0.0.1',
    },
    externals: {
      '@feature-hub/react': '^3.5.0',
      react: '*',
      'react-dom': '^17.0.2 || ^18.2.0',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  ownFeatureServiceDefinitions: [defineInPageNavigationService({ serviceEnabled: true })],

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const {
      's2:async-ssr-manager': asyncSsrManager,
      's2:serialized-state-manager': serializedStateManager,
      's2:logger': s2logger,
      'audi-content-service': contentService,
      'audi-render-mode-service': renderModeService,
      'audi-in-page-navigation-service': inPageNavigationService,
    } = featureServices;

    const logger = s2logger || console;
    logger.info('Feature App created.');

    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */
    const initialContent = contentService.getContent();
    const content = mapHeadlessContent(initialContent);

    const renderMode = renderModeService?.getRenderMode() || RenderMode.DEFAULT;

    let asyncStateHolder: AsyncStateHolder;

    if (asyncSsrManager) {
      // on the server
      asyncSsrManager.scheduleRerender(
        (async () => {
          if (content) {
            asyncStateHolder = await createInitialState(content, renderMode);
            serializedStateManager?.register(() => JSON.stringify(asyncStateHolder));
          }
        })(),
      );
    } else {
      // on the client
      const serializedState = serializedStateManager?.getSerializedState();

      if (serializedState) {
        asyncStateHolder = JSON.parse(serializedState) as AsyncStateHolder;
      } else {
        logger.warn(
          'Serialized state not found!. Possible reasons: \n 1. Running app in dev mode using the "static-demo-integrator" which does not support SSR \n 2. Running app in prod mode and SSR is broken',
        );
        asyncStateHolder = () => createInitialState(content, renderMode);
      }
    }

    return {
      render: () => {
        return (
          <UniversalEditorProvider
            contentService={contentService}
            renderModeService={renderModeService}
          >
            <ThemeProvider>
              <FeatureAppContextProvider
                logger={logger}
                inPageNavigationService={inPageNavigationService}
              >
                <App asyncStateHolder={asyncStateHolder} logger={logger} renderMode={renderMode} />
              </FeatureAppContextProvider>
            </ThemeProvider>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
